import { useEffect, useState } from 'react'
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { baseUrl } from '../../Environments/environment';
import { FiMessageSquare } from 'react-icons/fi';
import moment from 'moment';
import GuestListSkeleton from './GuestListSkeleton';
import { toast } from 'react-toastify';
import { FaArrowRight } from 'react-icons/fa';

function formatDateTime(dateTimeString) {
  if (!dateTimeString) return "";

  try {
    const date = moment.utc(dateTimeString);
    if (!date.isValid()) return "Invalid Date";

    const formattedDate = date.format("MMMM DD, YYYY");
    const formattedTime = date.format("h:mm A");

    return (
      <div className="flex flex-col">
        <span>{formattedDate}</span>
        <span>{formattedTime}</span>
      </div>
    );
  } catch (error) {
    console.error("Error formatting date time:", error);
    return dateTimeString;
  }
}

const GuestList = () => {
  const navigate = useNavigate();
  const params = useParams();
  const multiGuestSessionId = params.id;

  const [sessionDetails, setSessionDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchGuestList = async (multiGuestSessionId) => {
    try {
      const response = await axios.get(
        `${baseUrl}/guest/getGuestsByMultiSessionId/${multiGuestSessionId}`,
      );
      setSessionDetails(response.data?.result);
      localStorage.setItem("multiGuestSessionId", JSON.stringify(multiGuestSessionId));
      setLoading(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
      console.error("Error fetching guest data:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (multiGuestSessionId) {
      fetchGuestList(multiGuestSessionId);
    } else {
      setLoading(false);
    }
  }, []);

  const handleOpenMessagingApp = () => {
    const phoneNumber = sessionDetails.countryCode + sessionDetails.phoneNumber;
    const message = "Hello, I need some help!";
    const smsLink = `sms:${phoneNumber}?body=${encodeURIComponent(message)}`;

    window.location.href = smsLink;
  };

  return (
    <div className='fixed inset-0 flex flex-col overflow-y-auto'>
      <div className='flex-1 flex flex-col'>
        <header className='w-full mx-auto max-w-7xl px-2 sm:px-2 lg:divide-y lg:divide-gray-700 lg:px-4'>
          <div className='text-white flex h-16 justify-between items-center'>
            <p>
              {sessionDetails ? `${sessionDetails?.countryCode}${sessionDetails?.phoneNumber}` : ""}
            </p>
            <div className="relative flex px-3 justify-between">
              <button
                type="button"
                onClick={handleOpenMessagingApp}
                className="text-[#F9FAFB] text-[25px]"
              >
                <FiMessageSquare />
              </button>
            </div>
          </div>
        </header>
        <div className='bg-[#111827] text-white flex flex-col gap-4 flex-1 py-8 px-4 items-center'>
          {
            loading ? (
              <GuestListSkeleton />
            ) : (
              <>
                {
                  sessionDetails?.guests?.map((guest) => {
                    return (
                      <div className="overflow-hidden bg-[#F9FAFB] shadow sm:rounded-lg rounded-xl w-full max-w-96 p-4">
                        <div className="flex justify-between w-full">
                          <div className="flex flex-col items-start">
                            <p className='font-poppins text-lg text-blue-600'>{guest?.firstName} {guest?.lastName}</p>
                            <div className="font-poppins mt-2 text-[#9CA3AF] text-[13px]">
                              Current Status
                            </div>
                            <div
                              className={`font-poppins text-[21px] ${guest.status.reservationStatus === "Cancelled"
                                ? "text-red-500"
                                : guest.status.currentStatus === "Reservation"
                                  ? "text-[#10B981]"
                                  : guest.status.currentStatus === "In House"
                                    ? "text-[#F59E0B]"
                                    : guest.status.currentStatus === "Cancelled" ||
                                      guest.status.currentStatus === "No Show"
                                      ? "text-red-500"
                                      : "text-[#6366F1]"
                                }`}
                            >
                              {guest.status.reservationStatus === "Cancelled"
                                ? "Reservation Cancelled"
                                : guest.status.currentStatus === "Reservation"
                                  ? "Reservation Confirmed"
                                  : guest.status.currentStatus}
                            </div>
                          </div>
                          {(guest.status.currentStatus === "In House" ||
                            guest.status.currentStatus === "Checked Out") && (
                              <div className="px-4 py-2 sm:p-6 flex flex-col items-start">
                                <div className="font-poppins text-[#9CA3AF] text-[13px]">
                                  Room No
                                </div>
                                <div className="font-poppins text-[18px]">
                                  {guest.roomNumber}
                                </div>
                              </div>
                            )}
                        </div>
                        <div className="pt-0 flex sm:justify-start justify-between">
                          <div className="flex flex-col items-start pr-3">
                            <div className="font-poppins text-[#9CA3AF] text-[13px]">
                              {guest.status.earlyCheckInStatus === "Accepted"
                                ? "Early check in"
                                : "Check In"}
                            </div>
                            <div className="font-poppins text-[#111827] text-[14px]">
                              {formatDateTime(guest.checkIn)}
                            </div>
                          </div>
                          <div className="flex flex-col items-start pl-3">
                            <div className="font-poppins text-[#9CA3AF] text-[13px]">
                              {guest.status.lateCheckOutStatus === "Accepted"
                                ? "Late Check Out"
                                : guest.status.extendStayStatus === "Accepted"
                                  ? "Extended Stay"
                                  : "Check Out"}
                            </div>
                            <div className="font-poppins text-[#111827] text-[14px]">
                              {formatDateTime(guest.checkOut)}
                            </div>
                          </div>
                        </div>
                        <div>
                          <button onClick={() => navigate(`/${guest?.sessionId}`)} className="flex items-center gap-2 justify-center mt-4 bg-blue-500 rounded-md w-full px-2 py-2">
                            <span>View</span>
                            <FaArrowRight />
                          </button>
                        </div>
                      </div>
                    )
                  })
                }
              </>
            )
          }
        </div>
      </div>
    </div>
  )
}

export default GuestList